// material-certificate.component.ts
import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  Renderer2,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';

export enum CertificateType {
  CARBON = 1,
  FSC = 2,
  OTHER = 3,
}

interface Certification {
  id: number;
  name: string;
  typeId: number;
  typeName: string;
  isCustom: boolean;
}

interface CertificationType {
  id: number;
  type: string;
  label: string;
  options: string[];
  selected: string;
  customOption: string;
  customValue: string;
  isAdditional?: boolean;
  error?: string;
}

@Component({
  selector: 'piqet-angular-material-certificate',
  templateUrl: './material-certificate.component.html',
  styleUrls: ['./material-certificate.component.scss'],
})
export class MaterialCertificateComponent implements OnChanges, OnDestroy {
  @Output() certificationSelected = new EventEmitter<Certification>();
  @Input() existingCertifications: Certification[] = [];
  @ViewChildren('customInput') customInputs!: QueryList<ElementRef>;
  renderer = inject(Renderer2);
  CertificateType = CertificateType;
  lastSelectedCustomTypeId: number | null = null;
  private hasChanges = false; // Track if any changes were made

  certificationTypes: CertificationType[] = [
    {
      id: CertificateType.CARBON,
      type: 'CARBON_NEUTRAL',
      label: 'Carbon Certification',
      options: ['Carbon neutral', 'Carbon offset', 'Other carbon certification'],
      selected: 'Carbon neutral',
      customOption: 'Other carbon certification',
      customValue: '',
    },
    {
      id: CertificateType.FSC,
      type: 'FSC',
      label: 'Sustainable Forest Certification',
      options: ['FSC certification', 'PEFC', 'Other sustainable biomass certification'],
      selected: 'FSC certification',
      customOption: 'Other sustainable biomass certification',
      customValue: '',
    },
    {
      id: CertificateType.OTHER,
      type: 'OTHER',
      label: 'Add Additional Certification',
      options: [],
      selected: '',
      customOption: '',
      customValue: '',
      isAdditional: true,
    },
  ];

  ngOnDestroy(): void {
    if (!this.hasChanges) {
      return;
    }

    if (!this.certificationTypes || this.certificationTypes.length === 0) {
      return;
    }

    this.certificationTypes.forEach((certType, index) => {
      if (
        certType.selected?.trim() === certType.customOption?.trim() &&
        certType.customValue &&
        certType.customValue.trim() !== ''
      ) {
        const certification: Certification = {
          id: certType.id,
          name: `Custom certificate ${index + 1}`,
          typeId: certType.id,
          typeName: `Certification ${certType.id}`,
          isCustom: true,
        };
        this.certificationSelected.emit(certification);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['existingCertifications']) {
      this.bindExistingCertifications();
    }
  }

  ngAfterViewInit() {
    this.customInputs.changes.subscribe(() => {
      if (this.lastSelectedCustomTypeId !== null) {
        this.focusCustomInput(this.lastSelectedCustomTypeId);
      }
    });
  }

  private focusCustomInput(typeId: number): void {
    if (this.customInputs && this.customInputs.length > 0) {
      const inputIndex = this.certificationTypes.findIndex(type => type.id === typeId);
      if (inputIndex >= 0 && this.customInputs.get(inputIndex)) {
        const inputElement = this.customInputs.get(inputIndex)?.nativeElement;
        if (inputElement) {
          setTimeout(() => {
            this.renderer.selectRootElement(inputElement).focus();
          });
        }
      }
    }
  }

  private bindExistingCertifications(): void {
    if (!this.existingCertifications?.length) return;

    this.existingCertifications.forEach(cert => {
      const certType = this.certificationTypes.find(type => type.id === cert.typeId);
      if (certType) {
        if (cert.isCustom) {
          certType.selected = certType.customOption;
          certType.customValue = cert.name;
        } else {
          certType.selected = cert.name;
        }
      }
    });
  }

  selectOption(certType: CertificationType, option: string, isCheckError: boolean): void {
    if (certType.isAdditional) return;

    certType.selected = option;
    this.hasChanges = true;

    const isCustom = option === certType.customOption;
    if (isCustom) {
      this.lastSelectedCustomTypeId = certType.id;
      if (isCheckError && !certType.customValue?.trim()) {
        certType.error = 'Please enter certification name';
        return;
      }
      certType.error = '';
    } else {
      this.lastSelectedCustomTypeId = null;
    }

    const certification: Certification = {
      id: certType.id,
      name: isCustom ? certType.customValue : option,
      typeId: certType.id,
      typeName: `Certification ${certType.id}`,
      isCustom,
    };

    this.certificationSelected.emit(certification);
    this.hasChanges = false;
  }

  onAdditionalCertificationInput(certType: CertificationType): void {
    if (!certType.customValue?.trim()) {
      certType.error = 'Please enter certification name';
      return;
    }
    certType.error = '';
    this.hasChanges = true;

    if (certType.customValue.trim()) {
      const certification: Certification = {
        id: CertificateType.OTHER,
        name: certType.customValue,
        typeId: CertificateType.OTHER,
        typeName: 'Certification 3',
        isCustom: true,
      };
      this.certificationSelected.emit(certification);
      this.hasChanges = false;
    }
  }

  trackByFn(index: number): number {
    return index;
  }
}
